import React, { useEffect, useState ,useRef,useContext } from "react";
import apiAgent from "../../../../app/api/apiAgent";
import { toast } from "react-toastify";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faSpinner } from "@fortawesome/free-solid-svg-icons";
import ConfirmReportPurchaseModalPopup from "./ConfirmReportPurchaseModalPopup";
import { Button } from "@/app/common/common-components/ui/Button";
import { WhatsAppIcon } from "@/app/common/common-components/BrandIcons";
import { Mail, MessageCircle, Phone } from "lucide-react";
import { StoreContext } from "@/app/stores/store";
import { OMSPurchaseCredit } from "@/app/common/common-components/OMSPurchaseCredit";
// Custom toast notification functions
const showErrorToast = (message: string) => {
  // Use a div with custom styling to ensure the message is displayed cleanly
  toast.error(<div className="custom-toast-error">{message}</div>, {
    hideProgressBar: true,
    closeOnClick: true,
    pauseOnHover: true,
    draggable: true,
  });
};

const showInfoToast = (message: string) => {
  // Use a div with custom styling to ensure the message is displayed cleanly
  toast.info(<div className="custom-toast-info">{message}</div>, {
    hideProgressBar: true,
    closeOnClick: true,
    pauseOnHover: true,
    draggable: true,
  });
};

interface ReportOwnerDetailsProps {
  propertyId: string;
  propertyType: string;
  streetAddress: string;
  ownerDetailsData?: any; // Add optional ownerDetailsData prop
}

interface OwnerDetails {
  ownerName: string;
  ownerAddress: string;
  ownerContact: string;
  ownerEmail: string;
  purchaseDate?: string;
  purchasePrice?: number;
  ownerDetailsArray?: any[]; // Add field for the owner details array
  [key: string]: any; // Allow for additional properties
}

interface OwnerDetailItem {
  type?: string; // Renamed from leadType
  name?: string; // Combined name field
  fullAddress?: string;
  mobileNumber?: string;
  workNumber?: string;
  homeNumber?: string;
  emailAddress?: string;
  [key: string]: any; // Allow for additional properties
}

const ReportOwnerDetails: React.FC<ReportOwnerDetailsProps> = ({
  propertyId,
  propertyType,
  streetAddress,
  ownerDetailsData,
}) => {
  const [loading, setLoading] = useState(true);
  const [ownerDetails, setOwnerDetails] = useState<OwnerDetails | null>(null);
  const [ownerDetailsItems, setOwnerDetailsItems] = useState<OwnerDetailItem[]>(
    []
  );
  const [error, setError] = useState<string | null>(null);
  const [showPurchaseModal, setShowPurchaseModal] = useState(false);
  const [creditBalance, setCreditBalance] = useState(0);
  const [reportCost] = useState(5); // Assuming owner details cost 5 credits
  const purchaseModalRef = useRef<any>(null);
  const { userStore } = useContext(StoreContext);
  const currentUserID = userStore.userSession?.userId ?? "";

  useEffect(() => {
    // If ownerDetailsData is provided, use it directly
    if (ownerDetailsData) {
      setOwnerDetails(ownerDetailsData);
      processOwnerDetailsArray(ownerDetailsData);
      setLoading(false);
      return;
    }

    // Only fetch owner details if propertyId is provided and not empty
    if (propertyId) {
      // Otherwise fetch owner details from API
      const fetchOwnerDetails = async () => {
        try {
          setLoading(true);

          // Try to get the owner details
          let response;
          let wasGenerated = false;

          // Always try to generate owner details first for all properties
          // This ensures we don't get 404 errors for properties that don't have owner details yet
          if (streetAddress && propertyType) {
            try {
              console.log("Generating owner details for property:", propertyId);
              // Generate owner details
              response = await apiAgent.PropertyReports.generateOwnerDetails(
                propertyId,
                streetAddress,
                propertyType
              );
              console.log("Owner details generated successfully:", response);
              wasGenerated = true;

              // If we successfully generated the details, we don't want to show an error
              // So we'll process the response and return
              setOwnerDetails(response);
              processOwnerDetailsArray(response);
              setError(null);

              // Show a success message if the details were generated
              showInfoToast("Owner details generated successfully");
              setLoading(false);
              return;
            } catch (generateError) {
              console.error("Error generating owner details:", generateError);

              // If generation fails, try to fetch existing details as a fallback
              try {
                console.log(
                  "Trying to fetch existing owner details as fallback"
                );
                response =
                  await apiAgent.PropertyReports.getOwnerDetails(propertyId);
                console.log("Existing owner details found:", response);
              } catch (getError) {
                console.error(
                  "Error fetching existing owner details:",
                  getError
                );
                // If both generation and fetching fail, throw the generation error
                throw generateError;
              }
            }
          } else {
            // If we don't have the required data to generate owner details, try to fetch existing ones
            try {
              response =
                await apiAgent.PropertyReports.getOwnerDetails(propertyId);
            } catch (getError) {
              console.error(
                "Error fetching owner details and missing data for generation:",
                getError
              );
              throw getError;
            }
          }

          // This code will only run if we successfully fetched the details (not generated them)
          setOwnerDetails(response);
          processOwnerDetailsArray(response);
          setError(null);
        } catch (err: any) {
          console.error("Error fetching owner details:", err);
          setError(err.message || "Failed to fetch owner details");
          showErrorToast(err.message || "Failed to fetch owner details");
        } finally {
          setLoading(false);
        }
      };

      fetchOwnerDetails();
    } else {
      // If propertyId is not provided, set loading to false
      setLoading(false);
    }
  }, [propertyId, ownerDetailsData, streetAddress, propertyType]);

  // Helper function to format dates
  const formatDate = (dateString: string): string => {
    if (!dateString) return "";

    try {
      // Check if the date is in ISO format or just a date string
      const date = new Date(dateString);
      if (isNaN(date.getTime())) {
        return dateString; // Return as is if not a valid date
      }

      // Format as DD-MM-YYYY
      return date.toLocaleDateString("en-ZA", {
        day: "2-digit",
        month: "2-digit",
        year: "numeric",
      });
    } catch (e) {
      return dateString; // Return original string if formatting fails
    }
  };

  // Process the owner details array from the JSON string
  const processOwnerDetailsArray = (details: any) => {
    try {
      let parsedArray = null;

      // Handle different formats of owner details
      if (details) {
        // Case 1: If details.ownerDetails is a string, try to parse it
        if (details.ownerDetails && typeof details.ownerDetails === "string") {
          try {
            parsedArray = JSON.parse(details.ownerDetails);
          } catch (e) {
            console.error("Failed to parse ownerDetails string:", e);
          }
        }
        // Case 2: If details.ownerDetails is already an array
        else if (details.ownerDetails && Array.isArray(details.ownerDetails)) {
          parsedArray = details.ownerDetails;
        }
        // Case 3: If details itself is an array
        else if (Array.isArray(details)) {
          parsedArray = details;
        }
        // Case 4: If details is the owner details object directly
        else if (
          details.leadType ||
          details.type ||
          details.firstName ||
          details.lastName
        ) {
          parsedArray = [details];
        }
      }

      if (parsedArray && Array.isArray(parsedArray)) {
        // Transform the data - rename leadType to type and combine name fields
        const transformedItems = parsedArray.map((item) => {
          const transformedItem: OwnerDetailItem = { ...item };

          // Rename leadType to type
          if ("leadType" in transformedItem) {
            transformedItem.type = transformedItem.leadType;
            delete transformedItem.leadType;
          }

          // Combine name fields
          const firstName = transformedItem.firstName || "";
          const secondName = transformedItem.secondName || "";
          const thirdName = transformedItem.thirdName || "";
          const lastName = transformedItem.lastName || "";

          // Create full name, trimming extra spaces
          const fullName = [firstName, secondName, thirdName, lastName]
            .filter((namePart) => namePart.trim() !== "")
            .join(" ");

          if (fullName) {
            transformedItem.name = fullName;

            // Remove individual name fields
            delete transformedItem.firstName;
            delete transformedItem.secondName;
            delete transformedItem.thirdName;
            delete transformedItem.lastName;
          }

          // Combine mobile number with its last update date
          if (
            transformedItem.mobileNumber &&
            transformedItem.mobileLastUpdated
          ) {
            const formattedDate = formatDate(transformedItem.mobileLastUpdated);
            transformedItem.mobileNumber = `${transformedItem.mobileNumber} `;
            transformedItem.mobileNumberWithDate = {
              value: transformedItem.mobileNumber,
              updateDate: formattedDate,
            };
            delete transformedItem.mobileLastUpdated;
          }

          // Combine work number with its last update date
          if (transformedItem.workNumber && transformedItem.workLastUpdated) {
            const formattedDate = formatDate(transformedItem.workLastUpdated);
            transformedItem.workNumber = `${transformedItem.workNumber} `;
            transformedItem.workNumberWithDate = {
              value: transformedItem.workNumber,
              updateDate: formattedDate,
            };
            delete transformedItem.workLastUpdated;
          }

          // Combine home number with its last update date
          if (transformedItem.homeNumber && transformedItem.homeLastUpdated) {
            const formattedDate = formatDate(transformedItem.homeLastUpdated);
            transformedItem.homeNumber = `${transformedItem.homeNumber} `;
            transformedItem.homeNumberWithDate = {
              value: transformedItem.homeNumber,
              updateDate: formattedDate,
            };
            delete transformedItem.homeLastUpdated;
          }

          // Combine email with its last update date
          if (
            transformedItem.emailAddress &&
            transformedItem.emailLastUpdated
          ) {
            const formattedDate = formatDate(transformedItem.emailLastUpdated);
            transformedItem.emailAddress = `${transformedItem.emailAddress} `;
            transformedItem.emailAddressWithDate = {
              value: transformedItem.emailAddress,
              updateDate: formattedDate,
            };
            delete transformedItem.emailLastUpdated;
          }

          return transformedItem;
        });

        setOwnerDetailsItems(transformedItems);
      }
    } catch (err) {
      console.error("Error processing owner details array:", err);
    }
  };

  // Format field name for display
  const formatFieldName = (key: string): string => {
    // Special case for ID fields
    if (key.toLowerCase() === "id" || key.toLowerCase() === "idnumber") {
      return "ID Number";
    }

    return key
      .replace(/([A-Z])/g, " $1") // Add space before capital letters
      .replace(/^./, (str) => str.toUpperCase()) // Capitalize first letter
      .trim();
  };

  // Filter out internal fields that shouldn't be displayed
  const shouldDisplayField = (key: string): boolean => {
    const internalFields = [
      "__typename",
      "_id",
      "id",
      "createdAt",
      "updatedAt",
      "idNumber",
      "passportNumber",
      "mobileLastUpdated",
      "workLastUpdated",
      "homeLastUpdated",
      "emailLastUpdated",
    ];

    // If we have the WithDate version, don't show the original field
    const contactFields = [
      "mobileNumber",
      "workNumber",
      "homeNumber",
      "emailAddress",
    ];
    const withDateFields = contactFields.map((field) => `${field}WithDate`);

    // Check if this is a contact field and we have the WithDate version
    if (contactFields.includes(key)) {
      const withDateKey = `${key}WithDate`;
      const hasWithDateVersion = ownerDetailsItems.some(
        (item) => withDateKey in item
      );
      if (hasWithDateVersion) {
        return false;
      }
    }

    return !internalFields.includes(key);
  };

  // Sort fields to ensure type appears at the top
  const sortFields = (a: string, b: string): number => {
    if (a === "type") return -1;
    if (b === "type") return 1;
    if (a === "name") return -1;
    if (b === "name") return 1;
    return a.localeCompare(b);
  };

  // Safely get a contact field's display value, supporting optional "WithDate" shape
  const getContactValue = (
    detailItem: OwnerDetailItem,
    baseKey: string
  ): string | null => {
    const withDateKey = `${baseKey}WithDate` as keyof OwnerDetailItem;
    const withDate = (detailItem as any)[withDateKey];
    const rawValue =
      withDate && typeof withDate === "object" && (withDate as any).value
        ? (withDate as any).value
        : (detailItem as any)[baseKey];
    if (!rawValue) return null;
    return String(rawValue).trim();
  };

  // Sanitize numbers for different URI schemes
  const sanitizeForTel = (phone: string): string =>
    phone.replace(/[^\d+]/g, "");
  const sanitizeForWa = (phone: string): string => phone.replace(/\D/g, "");

  const renderContactActions = (detailItem: OwnerDetailItem) => {
    const mobile = getContactValue(detailItem, "mobileNumber");
    const work = getContactValue(detailItem, "workNumber");
    const home = getContactValue(detailItem, "homeNumber");
    const email = getContactValue(detailItem, "emailAddress");
    const primaryPhone = mobile || work || home;

    if (!email && !primaryPhone) return null;

    return (
      <div className="tw-mt-2 tw-flex tw-flex-wrap tw-items-center tw-gap-2">
        {primaryPhone && (
          <>
            <Button size="icon" variant="secondary" asChild>
              <a href={`tel:${sanitizeForTel(primaryPhone)}`} aria-label="Call">
                <Phone className="tw-size-4" />
              </a>
            </Button>
            <Button size="icon" variant="secondary" asChild>
              <a href={`sms:${sanitizeForTel(primaryPhone)}`} aria-label="SMS">
                <MessageCircle className="tw-size-4" />
              </a>
            </Button>
            <Button size="icon" variant="secondary" asChild>
              <a
                href={`https://wa.me/${sanitizeForWa(primaryPhone)}`}
                target="_blank"
                rel="noopener noreferrer"
                aria-label="WhatsApp"
              >
                <WhatsAppIcon className="tw-size-4" />
              </a>
            </Button>
          </>
        )}
        {email && (
          <Button size="icon" variant="secondary" asChild>
            <a href={`mailto:${email}`} aria-label="Email">
              <Mail className="tw-size-4" />
            </a>
          </Button>
        )}
      </div>
    );
  };

  // Function to handle purchase button click
  const handlePurchaseClick = () => {
    // Get user credit balance
    apiAgent.PropertyReports.getUserCreditBalance()
      .then((response) => {
        // Extract the credit balance correctly from the response
        const integerCreditBalance = Math.floor(response);
        setCreditBalance(integerCreditBalance);
        setShowPurchaseModal(true);
      })
      .catch((err) => {
        console.error("Error fetching credit balance:", err);
        setError(err.message || "Failed to fetch credit balance");
        showErrorToast(err.message || "Failed to fetch credit balance");
      });
  };

  // Function to confirm purchase
  const confirmPurchase = () => {
    // Check if the user has enough credits
    if (creditBalance < reportCost) {
      showErrorToast("Not enough credits to generate owner details.");
      setShowPurchaseModal(false);
      return;
    }

    setShowPurchaseModal(false);
    setLoading(true);

    // Call API to generate owner details
    apiAgent.PropertyReports.generateOwnerDetails(
      propertyId,
      streetAddress,
      propertyType
    )
      .then((response) => {
        setOwnerDetails(response);
        processOwnerDetailsArray(response);
        setError(null);
        // Remove success toast notification
      })
      .catch((err) => {
        toast.error(
          <div>
            Owner contact details are not available for this property. For more
            info,{" "}
            <a
              href="https://help.entegral.net/en/articles/9890912-insights-overview#h_aefee2ab92"
              target="_blank"
              rel="noopener noreferrer"
              style={{ color: "#fff", textDecoration: "underline" }}
            >
              click here
            </a>
            <div>Ref # 111</div>
          </div>
        );
      })
      .finally(() => {
        setLoading(false);
      });
  };

  // Function to open purchase credits modal
  const openPurchaseCreditsModal = () => {
    setShowPurchaseModal(false);
    if (purchaseModalRef.current) {
      purchaseModalRef.current.onOpen(currentUserID);
    }
  };

  const OnPurchaseCreditSuccess = (credit: number | undefined) => {
      setCreditBalance(credit ?? 0);
  };


  // Custom error component to show when there's an error
  const ErrorDisplay = ({ message }: { message: string }) => (
    <div className="alert alert-danger">
      <h4>Error Loading Owner Details</h4>
      <p>{message}</p>
      <p>Please try again or contact support if the problem persists.</p>
    </div>
  );

  // Loading component
  const LoadingDisplay = () => (
    <div className="text-center p-4">
      <div className="spinner-border text-primary" role="status">
        <span className="sr-only">Loading...</span>
      </div>
      <p className="mt-2">Loading owner details...</p>
    </div>
  );

  // Main render
  if (loading) {
    return <LoadingDisplay />;
  }

  if (error) {
    return <ErrorDisplay message={error} />;
  }

  return (
    <div className="report-owner-details-container">
      <h2>Owner Details</h2>
      <p className="property-address">
        <strong>Address:</strong> {streetAddress}
      </p>
      <div className="report-owner-details-content full-width">
        <div className="report-owner-details-owner-info">
          {ownerDetailsItems.length > 0 ? (
            <>
              {ownerDetailsItems.map((item, index) => (
                <div key={index} className="owner-detail-item">
                  {Object.entries(item)
                    .filter(([key]) => shouldDisplayField(key))
                    .sort(([keyA], [keyB]) => sortFields(keyA, keyB))
                    .map(([key, value]) => {
                      const phoneFields = [
                        "mobileNumber",
                        "workNumber",
                        "homeNumber",
                      ];
                      const isWithDate =
                        key.endsWith("WithDate") &&
                        typeof value === "object" &&
                        (value as any).value;
                      const baseKey = isWithDate
                        ? key.replace("WithDate", "")
                        : key;

                      // Phone fields (mobile/work/home)
                      if (phoneFields.includes(baseKey)) {
                        const numberStr = String(
                          isWithDate ? (value as any).value : value || ""
                        ).trim();
                        if (!numberStr) return null;

                        return (
                          <p key={key} className="contact-field">
                            <strong>{formatFieldName(baseKey)}:</strong>{" "}
                            {numberStr}
                            <span className="tw-ml-2 tw-inline-flex tw-items-center tw-gap-2">
                              <a
                                href={`tel:${sanitizeForTel(numberStr)}`}
                                aria-label="Call"
                                className="tw-text-primary"
                              >
                                <Phone className="tw-size-4 tw-text-secondary-foreground" />
                              </a>
                              <a
                                href={`sms:${sanitizeForTel(numberStr)}`}
                                aria-label="SMS"
                                className="tw-text-primary"
                              >
                                <MessageCircle className="tw-size-4 tw-text-secondary-foreground" />
                              </a>
                              <a
                                href={`https://wa.me/${sanitizeForWa(numberStr)}`}
                                target="_blank"
                                rel="noopener noreferrer"
                                aria-label="WhatsApp"
                                className="tw-text-primary"
                              >
                                <WhatsAppIcon className="tw-size-4 tw-text-secondary-foreground" />
                              </a>
                            </span>
                            {isWithDate && (
                              <span className="update-date tw-ml-2">
                                (Updated: {(value as any).updateDate})
                              </span>
                            )}
                          </p>
                        );
                      }

                      // Email field
                      if (baseKey === "emailAddress") {
                        const emailStr = String(
                          isWithDate ? (value as any).value : value || ""
                        ).trim();
                        if (!emailStr) return null;

                        return (
                          <p key={key} className="contact-field">
                            <strong>{formatFieldName(baseKey)}:</strong>{" "}
                            {emailStr}
                            <span className="tw-ml-2 tw-inline-flex tw-items-center tw-gap-2">
                              <a
                                href={`mailto:${emailStr}`}
                                aria-label="Email"
                                className="tw-text-primary"
                              >
                                <Mail className="tw-size-4 tw-text-secondary-foreground" />
                              </a>
                            </span>
                            {isWithDate && (
                              <span className="update-date tw-ml-2">
                                (Updated: {(value as any).updateDate})
                              </span>
                            )}
                          </p>
                        );
                      }

                      // Default rendering
                      return (
                        <p key={key}>
                          <strong>{formatFieldName(key)}:</strong>{" "}
                          {value || "Not available"}
                        </p>
                      );
                    })}
                </div>
              ))}
            </>
          ) : (
            <div>
              <p>
                No owner details available. Would you like to purchase owner
                details for this property?
              </p>
              <button
                className="btn btn-primary mt-3"
                onClick={handlePurchaseClick}
              >
                Purchase Owner Details
              </button>
            </div>
          )}
        </div>
      </div>

      {/* Confirmation Modal for Owner Details Purchase */}
      <ConfirmReportPurchaseModalPopup
        showModal={showPurchaseModal}
        title="Generate Owner Details"
        creditBalance={creditBalance}
        reportCost={reportCost}
        closeModalPopup={() => setShowPurchaseModal(false)}
        confirmModalPopup={confirmPurchase}
        openPurchaseCreditsModal={openPurchaseCreditsModal}
      />
      <OMSPurchaseCredit ref={purchaseModalRef} ID="PropertyReportsPurchase" OnSuccess={OnPurchaseCreditSuccess} />
    </div>
  );
};

export default ReportOwnerDetails;
